import { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useVenueId } from '../../hooks/useUserData';
import { fetchShiftHistory } from '../../utils/shiftService';
import AdminPageHeader from '../shared/AdminPageHeader';
import AdminSectionCard from '../shared/AdminSectionCard';
import AdminEmptyState from '../shared/AdminEmptyState';
import AdminNotice from '../shared/AdminNotice';
import '../../styles/route_pages/Shifts.css';

const formatCurrency = (value) =>
  new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', maximumFractionDigits: 2 }).format(value || 0);

const formatDateTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('tr-TR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const varianceClass = (v) => {
  if (v == null) return '';
  if (v > 0) return 'shifts-variance--surplus';
  if (v < 0) return 'shifts-variance--deficit';
  return 'shifts-variance--match';
};

const varianceText = (v) => {
  if (v == null) return '—';
  const prefix = v > 0 ? '+' : '';
  return `${prefix}${formatCurrency(v)}`;
};

const Shifts = () => {
  const venueId = useVenueId();
  const { getAccessTokenSilently } = useAuth0();
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedShift, setSelectedShift] = useState(null);

  useEffect(() => {
    let active = true;

    if (!venueId) {
      setError('İşletme bağlamı bulunamadı.');
      setLoading(false);
      return;
    }

    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const data = await fetchShiftHistory(venueId, getAccessTokenSilently);
        if (!active) return;
        setShifts(data || []);
      } catch (err) {
        if (!active) return;
        setError(err.message || 'Vardiya geçmişi yüklenemedi.');
      } finally {
        if (active) setLoading(false);
      }
    };

    load();
    return () => { active = false; };
  }, [venueId, getAccessTokenSilently]);

  return (
    <div className="page-container shifts-page">
      <AdminPageHeader
        title="Vardiyalar"
        subtitle="Vardiya geçmişi ve kasa mutabakat kayıtları."
      />

      {error && (
        <AdminNotice tone="error" title="Yüklenemedi" message={error} />
      )}

      {loading ? (
        <AdminSectionCard title="Yükleniyor" subtitle="Vardiya verileri hazırlanıyor." className="shifts-state-card">
          <div style={{ textAlign: 'center', color: 'var(--text-secondary)', fontSize: 'var(--font-size-sm)' }}>
            Veriler hazırlanıyor...
          </div>
        </AdminSectionCard>
      ) : shifts.length === 0 ? (
        <AdminSectionCard title="Vardiya Geçmişi" subtitle="Bu mekanda henüz vardiya kaydı bulunmuyor.">
          <AdminEmptyState
            title="Vardiya kaydı yok"
            description="Mekan panelinden vardiya açılıp kapatıldığında burada görünecektir."
          />
        </AdminSectionCard>
      ) : (
        <AdminSectionCard title="Vardiya Geçmişi" subtitle={`Toplam ${shifts.length} vardiya kaydı.`}>
          <div className="shifts-table-wrap">
            <table className="shifts-table">
              <thead>
                <tr>
                  <th>Tarih</th>
                  <th>Açan</th>
                  <th>Kapatan</th>
                  <th>Açılış Kasa</th>
                  <th>Toplam Ciro</th>
                  <th>Beklenen Nakit</th>
                  <th>Gerçek Nakit</th>
                  <th>Fark</th>
                  <th>Durum</th>
                </tr>
              </thead>
              <tbody>
                {shifts.map((s) => (
                  <tr key={s.id} onClick={() => setSelectedShift(s)} style={{ cursor: 'pointer' }}>
                    <td>{formatDateTime(s.openedAt)}</td>
                    <td>{s.openedByUserId}</td>
                    <td>{s.closedByUserId || '—'}</td>
                    <td>{formatCurrency(s.openingCash)}</td>
                    <td>{s.paymentSummary ? formatCurrency(s.paymentSummary.totalRevenue) : '—'}</td>
                    <td>{s.expectedCash != null ? formatCurrency(s.expectedCash) : '—'}</td>
                    <td>{s.actualCash != null ? formatCurrency(s.actualCash) : '—'}</td>
                    <td className={varianceClass(s.variance)}>{varianceText(s.variance)}</td>
                    <td>
                      <span className={`shifts-status-badge shifts-status-badge--${s.status}`}>
                        {s.status === 'open' ? 'Açık' : 'Kapalı'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </AdminSectionCard>
      )}

      {selectedShift && (
        <div className="shifts-detail-overlay" onClick={() => setSelectedShift(null)}>
          <div className="shifts-detail-card" onClick={(e) => e.stopPropagation()}>
            <div className="shifts-detail-header">
              <h3>Vardiya Detayı</h3>
              <span className={`shifts-status-badge shifts-status-badge--${selectedShift.status}`}>
                {selectedShift.status === 'open' ? 'Açık' : 'Kapalı'}
              </span>
            </div>

            <div className="shifts-detail-body">
              <div className="shifts-detail-section">
                <div className="shifts-detail-section__title">Zaman ve Personel</div>
                <div className="shifts-detail-section__rows">
                  <div className="shifts-detail-row">
                    <span className="shifts-detail-row__label">Açılış</span>
                    <span className="shifts-detail-row__value">{formatDateTime(selectedShift.openedAt)}</span>
                  </div>
                  <div className="shifts-detail-row">
                    <span className="shifts-detail-row__label">Kapanış</span>
                    <span className="shifts-detail-row__value">{formatDateTime(selectedShift.closedAt)}</span>
                  </div>
                  <div className="shifts-detail-row">
                    <span className="shifts-detail-row__label">Açan</span>
                    <span className="shifts-detail-row__value">{selectedShift.openedByUserId}</span>
                  </div>
                  <div className="shifts-detail-row">
                    <span className="shifts-detail-row__label">Kapatan</span>
                    <span className="shifts-detail-row__value">{selectedShift.closedByUserId || '—'}</span>
                  </div>
                </div>
              </div>

              <div className="shifts-detail-section">
                <div className="shifts-detail-section__title">Ödeme Dağılımı</div>
                <div className="shifts-detail-section__rows">
                  <div className="shifts-detail-row">
                    <span className="shifts-detail-row__label">Nakit Ödemeler</span>
                    <span className="shifts-detail-row__value">{selectedShift.paymentSummary ? formatCurrency(selectedShift.paymentSummary.totalCash) : '—'}</span>
                  </div>
                  <div className="shifts-detail-row">
                    <span className="shifts-detail-row__label">Kart Ödemeler</span>
                    <span className="shifts-detail-row__value">{selectedShift.paymentSummary ? formatCurrency(selectedShift.paymentSummary.totalCard) : '—'}</span>
                  </div>
                  <div className="shifts-detail-row">
                    <span className="shifts-detail-row__label">Diğer Ödemeler</span>
                    <span className="shifts-detail-row__value">{selectedShift.paymentSummary ? formatCurrency(selectedShift.paymentSummary.totalOther) : '—'}</span>
                  </div>
                  <div className="shifts-detail-row">
                    <span className="shifts-detail-row__label">Toplam Ciro</span>
                    <span className="shifts-detail-row__value">{selectedShift.paymentSummary ? formatCurrency(selectedShift.paymentSummary.totalRevenue) : '—'}</span>
                  </div>
                  <div className="shifts-detail-row">
                    <span className="shifts-detail-row__label">Kapanan Adisyon</span>
                    <span className="shifts-detail-row__value">{selectedShift.paymentSummary ? selectedShift.paymentSummary.checksClosedCount : '—'}</span>
                  </div>
                </div>
              </div>

              <div className="shifts-detail-section">
                <div className="shifts-detail-section__title">Kasa Mutabakatı</div>
                <div className="shifts-detail-section__rows">
                  <div className="shifts-detail-row">
                    <span className="shifts-detail-row__label">Açılış Kasa</span>
                    <span className="shifts-detail-row__value">{formatCurrency(selectedShift.openingCash)}</span>
                  </div>
                  <div className="shifts-detail-row">
                    <span className="shifts-detail-row__label">Beklenen Nakit</span>
                    <span className="shifts-detail-row__value">{selectedShift.expectedCash != null ? formatCurrency(selectedShift.expectedCash) : '—'}</span>
                  </div>
                  <div className="shifts-detail-row">
                    <span className="shifts-detail-row__label">Gerçek Nakit</span>
                    <span className="shifts-detail-row__value">{selectedShift.actualCash != null ? formatCurrency(selectedShift.actualCash) : '—'}</span>
                  </div>
                  <div className="shifts-detail-row shifts-detail-row--variance">
                    <span className="shifts-detail-row__label">Fark</span>
                    <span className={`shifts-detail-row__value ${varianceClass(selectedShift.variance)}`}>{varianceText(selectedShift.variance)}</span>
                  </div>
                </div>
              </div>

              {selectedShift.notes && (
                <div className="shifts-detail-notes">
                  <strong>Notlar:</strong> {selectedShift.notes}
                </div>
              )}
            </div>

            <div className="shifts-detail-footer">
              <button
                type="button"
                className="shifts-detail-close-btn"
                onClick={() => setSelectedShift(null)}
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Shifts;
